import React, { useMemo } from 'react'

export type WaitingParticipant = {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  status: 'invited' | 'joined' | 'active' | 'completed' | 'left'
  joinedAt: string | null
}

type ParticipantWaitListProps = {
  participants: WaitingParticipant[]
  currentUserEmail: string
  className?: string
}

export const ParticipantWaitList: React.FC<ParticipantWaitListProps> = ({
  participants,
  currentUserEmail,
  className = '',
}) => {
  const { joinedParticipants, pendingCount } = useMemo(() => {
    const joined = participants.filter(
      (p) => p.status === 'joined' || p.status === 'active'
    )
    const pending = participants.filter(
      (p) => p.status === 'invited'
    ).length

    return {
      joinedParticipants: joined,
      pendingCount: pending,
    }
  }, [participants])

  const getParticipantName = (participant: WaitingParticipant): string => {
    if (participant.firstName && participant.lastName) {
      return `${participant.firstName} ${participant.lastName}`
    }
    if (participant.firstName) {
      return participant.firstName
    }
    // Fall back to email prefix
    return participant.email.split('@')[0]
  }

  const getInitials = (participant: WaitingParticipant): string => {
    if (participant.firstName && participant.lastName) {
      return `${participant.firstName[0]}${participant.lastName[0]}`.toUpperCase()
    }
    if (participant.firstName) {
      return participant.firstName.slice(0, 2).toUpperCase()
    }
    return participant.email.slice(0, 2).toUpperCase()
  }

  const isCurrentUser = (participant: WaitingParticipant): boolean => {
    return participant.email.toLowerCase() === currentUserEmail.toLowerCase()
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-5 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-900">Participants</h3>
        <span className="text-sm text-gray-500">
          {joinedParticipants.length} of {participants.length} joined
        </span>
      </div>

      <div className="space-y-2">
        {/* Joined participants */}
        {joinedParticipants.map((participant) => (
          <div
            key={participant.id}
            className={`flex items-center gap-3 p-2 rounded-lg ${
              isCurrentUser(participant)
                ? 'bg-blue-50 border border-blue-100'
                : 'bg-gray-50'
            }`}
          >
            {/* Avatar */}
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                isCurrentUser(participant)
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-200 text-gray-600'
              }`}
            >
              {getInitials(participant)}
            </div>

            {/* Name and status */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900 truncate">
                  {getParticipantName(participant)}
                </span>
                {isCurrentUser(participant) && (
                  <span className="text-xs text-blue-600 font-medium">(You)</span>
                )}
              </div>
            </div>

            {/* Connected indicator */}
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              <span className="text-xs text-gray-500">Connected</span>
            </div>
          </div>
        ))}

        {/* Pending participants indicator */}
        {pendingCount > 0 && (
          <div className="flex items-center gap-3 p-2 rounded-lg bg-gray-50 border border-dashed border-gray-200">
            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
            <div className="flex-1">
              <span className="text-sm text-gray-500">
                {pendingCount} more participant{pendingCount !== 1 ? 's' : ''} expected
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 bg-gray-300 rounded-full" />
              <span className="text-xs text-gray-400">Waiting</span>
            </div>
          </div>
        )}

        {/* Empty state */}
        {joinedParticipants.length === 0 && pendingCount === 0 && (
          <div className="text-center py-4">
            <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <p className="text-sm text-gray-500">No participants yet</p>
          </div>
        )}
      </div>

      {/* Help text */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <p className="text-xs text-gray-500 text-center">
          Participants will appear here as they join the waiting room
        </p>
      </div>
    </div>
  )
}

export default ParticipantWaitList
